import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, X, Crown, Loader2, Briefcase, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssigneeAvatar } from "@/components/EngagementChip";

const JOB = { owner: "Owner", admin: "Admin", chief_estimator: "Chief estimator", estimator: "Estimator", business_development: "Business development", project_manager: "Project manager" };

// Header chip: who leads + helpers at a glance (and whose move it is); click opens the team dialog. kind: "bids" | "leads".
export function BidTeamChip({ bidId, kind = "bids", compact, onChanged }) {
  const [team, setTeam] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get(`/${kind}/${bidId}/team`).then((r) => setTeam(r.data)).catch(() => {}); }, [bidId, kind]);
  useEffect(() => { load(); }, [load]);
  const members = team?.members || [];
  const lead = members.find((m) => m.is_lead);
  const others = members.filter((m) => !m.is_lead);
  const ball = team?.ball;
  const title = ball?.name ? `Ball in court: ${ball.name} · ${ball.why}` : "Who is on this";
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="bid-team-chip" title={title}
        className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[11px] text-slate-600 shrink-0">
        <Users className="w-3.5 h-3.5 text-[#003E87]" />
        {lead ? <span className="flex items-center gap-1"><AssigneeAvatar assignee={lead} size="w-4 h-4" testid="bid-team-lead-avatar" />{!compact && <span className="font-medium text-slate-800 max-w-[120px] truncate">{lead.name}</span>}</span> : <span className="text-amber-700">No lead</span>}
        {others.length > 0 && <span className="flex -space-x-1">{others.slice(0, 3).map((m) => <AssigneeAvatar key={m.user_id} assignee={m} size="w-4 h-4" />)}</span>}
        {others.length > 3 && <span>+{others.length - 3}</span>}
        {!compact && ball?.name && <span className="hidden xl:inline text-slate-400" data-testid="bid-team-ball">· ball: <span className="text-slate-700">{ball.name}</span></span>}
        {!compact && others.length === 0 && !ball?.name && team?.can_manage && <span className="text-slate-400">· add</span>}
      </button>
      {open && <BidTeamDialog bidId={bidId} kind={kind} team={team} onClose={() => setOpen(false)} onChanged={() => { load(); onChanged && onChanged(); }} />}
    </>
  );
}

function Picker({ users, exclude, value, onChange, placeholder, testid }) {
  const ooo = (u) => u.out_of_office?.active ? ` · out until ${u.out_of_office.until || "?"}` : "";
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 text-sm flex-1"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {users.filter((u) => !exclude.has(u.user_id)).map((u) => <SelectItem key={u.user_id} value={u.user_id} data-testid={`${testid}-${u.user_id}`}>{u.name}{u.role ? ` · ${JOB[u.role] || u.role}` : ""}{ooo(u)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function BidTeamDialog({ bidId, kind = "bids", team, onClose, onChanged }) {
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState("");
  const [role, setRole] = useState("estimator");
  const [leadPick, setLeadPick] = useState("");
  const [bdPick, setBdPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/users/lean").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const members = team?.members || [];
  const onTeam = new Set(members.map((m) => m.user_id));
  const lead = members.find((m) => m.is_lead);
  const bdLead = members.find((m) => m.is_bd_lead);
  const isBid = kind === "bids";
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged && onChanged(); setPick(""); setLeadPick(""); setBdPick(""); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Could not update the team"); }
    finally { setBusy(false); }
  };
  const add = () => run(() => api.post(`/${kind}/${bidId}/team`, { user_id: pick, role }), "Added to the team");
  const remove = (m) => run(() => api.delete(`/${kind}/${bidId}/team/${m.user_id}`), `${m.name} removed`);
  const setLead = () => run(() => api.put(`/${kind}/${bidId}/team/lead`, { user_id: leadPick }), isBid ? "Lead estimator changed" : "BD lead changed");
  const setBd = () => run(() => api.put(`/bids/${bidId}/team/bd-lead`, { user_id: bdPick }), "BD lead changed");
  const roles = Object.entries(team?.roles || {}).filter(([k]) => (isBid || k !== "bd_lead") && (team?.can_change_lead || !["bd_lead", "approver"].includes(k)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bid-team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4 text-[#003E87]" /> {isBid ? "Bid team" : "Pursuit team"}</DialogTitle>
          <DialogDescription>{isBid ? "Who is working this bid. Everyone here can open it; the lead estimator owns the estimate, the BD lead owns the client." : "Who is working this pursuit. The BD lead owns the client; helpers carry into the bid at hand-off."}</DialogDescription>
        </DialogHeader>
        {team?.ball?.name && (
          <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-900" data-testid="bid-team-ball-row">
            <Target className="w-3.5 h-3.5" /> Ball in court: <b>{team.ball.name}</b> · {team.ball.why}
          </div>
        )}
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-[36vh] overflow-y-auto" data-testid="bid-team-members">
          {members.length === 0 && <div className="px-3 py-3 text-sm text-slate-500" data-testid="bid-team-empty">Nobody is assigned yet.</div>}
          {members.map((m) => (
            <div key={m.user_id} className="px-3 py-2 flex items-center gap-3" data-testid={`bid-team-member-${m.user_id}`}>
              <AssigneeAvatar assignee={m} size="w-7 h-7" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">{m.name}{m.is_lead && <Crown className="w-3.5 h-3.5 text-[#F4C400]" />}{m.is_bd_lead && !m.is_lead && <Briefcase className="w-3.5 h-3.5 text-[#003E87]" />}</div>
                <div className="text-[11px] text-slate-500">{m.role_label}{m.job_role ? ` · ${JOB[m.job_role] || m.job_role}` : ""}{m.added_by_name ? ` · added by ${m.added_by_name}` : ""}</div>
              </div>
              {!m.is_lead && team?.can_manage && (
                <button onClick={() => remove(m)} disabled={busy} data-testid={`bid-team-remove-${m.user_id}`} className="p-1 text-slate-400 hover:text-rose-600" title="Remove"><X className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
        {team?.can_manage ? (
          <div className="space-y-3 pt-1">
            <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="bid-team-add">
              <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> Add a teammate</div>
              <div className="flex gap-2">
                <Picker users={users} exclude={onTeam} value={pick} onChange={setPick} placeholder="Who?" testid="bid-team-add-user" />
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="bid-team-add-role" className="h-9 text-sm w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map(([k, v]) => <SelectItem key={k} value={k} data-testid={`bid-team-add-role-${k}`}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={add} disabled={!pick || busy} data-testid="bid-team-add-btn" className="h-9 bg-[#003E87] hover:bg-[#00305F] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button>
              </div>
              {isBid && <div className="text-[11px] text-slate-400">An <b>Approver</b> shows up in the sign-off picker for this bid. A <b>BD lead</b> owns the client and the follow-up after send.</div>}
            </div>
            {team.can_change_lead && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="bid-team-lead">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-[#F4C400]" /> {isBid ? "Change the lead estimator" : "Change the BD lead"}</div>
                <div className="flex gap-2">
                  <Picker users={users} exclude={new Set(lead ? [lead.user_id] : [])} value={leadPick} onChange={setLeadPick} placeholder={isBid ? "New lead estimator" : "New BD lead"} testid="bid-team-lead-user" />
                  <Button onClick={setLead} disabled={!leadPick || busy} variant="outline" data-testid="bid-team-lead-btn" className="h-9">Make lead</Button>
                </div>
                {isBid && <div className="text-[11px] text-slate-400">The previous lead stays on the bid as an estimator. The new lead is emailed.</div>}
              </div>
            )}
            {isBid && team.can_change_lead && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="bid-team-bd">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-[#003E87]" /> {bdLead ? `BD lead: ${bdLead.name}` : "No BD lead yet"}</div>
                <div className="flex gap-2">
                  <Picker users={users} exclude={new Set(bdLead ? [bdLead.user_id] : [])} value={bdPick} onChange={setBdPick} placeholder="Who owns the client?" testid="bid-team-bd-user" />
                  <Button onClick={setBd} disabled={!bdPick || busy} variant="outline" data-testid="bid-team-bd-btn" className="h-9">Set BD lead</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500" data-testid="bid-team-readonly">Ask the lead, business development or leadership to change the team.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Stacked avatars for lists and cards: the lead first, then the helpers.
export function TeamAvatars({ assignee, team, testid, max = 3 }) {
  const rest = (team || []).filter((m) => m && m.user_id !== assignee?.user_id);
  if (!assignee && rest.length === 0) return null;
  return (
    <span className="inline-flex items-center -space-x-1.5" data-testid={testid} title={[assignee?.name, ...rest.map((m) => m.name)].filter(Boolean).join(", ")}>
      {assignee && <AssigneeAvatar assignee={assignee} />}
      {rest.slice(0, max).map((m) => <AssigneeAvatar key={m.user_id} assignee={m} />)}
      {rest.length > max && <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 text-[9px] font-semibold grid place-items-center ring-1 ring-white">+{rest.length - max}</span>}
    </span>
  );
}
